import {useEffect, useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';

import {
  AssignmentGroup,
  AssignmentGroupValue,
  PlateMapWellDisplayLayer,
  PlateMap,
  PlateMapDisplayLayerAttribute,
} from 'samples/plate-maps/PlateMapConstants';
import {
  getDisplayLayerForAttribute,
  createAssignmentGroupsForAttribute,
  getDisplayLayerAttributeKey,
} from 'samples/plate-maps/PlateMapUtil';
import usePrevious from 'ui-library/hooks/usePrevious';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';

export interface PlateMapDisplayAttributeState {
  assignmentGroupsPerAttribute: {
    [key: string]: {[id: string]: AssignmentGroup<AssignmentGroupValue>};
  };
  setAssignmentGroupsPerAttribute: (groups: {
    [key: string]: {[id: string]: AssignmentGroup<AssignmentGroupValue>};
  }) => void;
  selectedAttribute: PlateMapDisplayLayerAttribute;
  setSelectedAttribute: (attr: PlateMapDisplayLayerAttribute) => void;
  displayLayer: PlateMapWellDisplayLayer;
}

/**
 * A hook that handles the state for the assignment groups, display layer and selected display attribute
 * for viewing a plate map.
 *
 * @param plateMap The current state of the plate and its contents, schema tags etc.
 * @param initialSelectedAttribute The display attribute that is initially selected
 * @param autoUpdateGroups Whether to update the plate's legend groups automatically as the plate map
 *        well data changes. If false, initial assignment groups are created from the plate map,
 *        and the component is expected to update assignment groups with setAssignmentGroupsPerAttribute.
 */
export default function usePlateMapDisplayAttributeState(
  plateMap: PlateMap,
  initialSelectedAttribute: PlateMapDisplayLayerAttribute,
  autoUpdateGroups: boolean,
  initialAssignmentGroups?: {
    [key: string]: {[id: string]: AssignmentGroup<AssignmentGroupValue>};
  }
): PlateMapDisplayAttributeState {
  const previousPlateMap = usePrevious(plateMap);
  // Which attribute is currently being viewed on the plate map
  const [selectedAttribute, setSelectedAttribute] =
    useState<PlateMapDisplayLayerAttribute>(initialSelectedAttribute);
  // Keep track of all assignment groups to keep display information consistent for each schema value when switching views.
  const [assignmentGroupsPerAttribute, setAssignmentGroupsPerAttribute] = useStateIfMounted<{
    [key: string]: {[id: string]: AssignmentGroup<AssignmentGroupValue>};
  }>(initialAssignmentGroups ?? {});
  const displayLayer = useMemo(() => getDisplayLayerForAttribute(selectedAttribute), [selectedAttribute]);
  const [fetchingAssignmentGroups, setFetchingAssignmentGroups] = useState(false);

  useEffect(() => {
    // If assignment groups should be autoupdated and the plate map information has changed,
    // reset all assignment groups so that they can be correctly recreated with any new information.
    if (autoUpdateGroups && !isEqual(previousPlateMap, plateMap) && Boolean(previousPlateMap)) {
      setAssignmentGroupsPerAttribute({});
    }
  }, [autoUpdateGroups, plateMap, previousPlateMap, setAssignmentGroupsPerAttribute]);

  useEffect(() => {
    // Set assignment groups for the selected attribute annotation layer if they don't already exist.
    const attributeKey = getDisplayLayerAttributeKey(selectedAttribute);
    const createAssignmentGroups = async (): Promise<void> => {
      const assignmentGroups = await createAssignmentGroupsForAttribute(plateMap, selectedAttribute);
      setAssignmentGroupsPerAttribute({...assignmentGroupsPerAttribute, [attributeKey]: assignmentGroups});
      setFetchingAssignmentGroups(false);
    };
    if (!Object.keys(assignmentGroupsPerAttribute).includes(attributeKey) && !fetchingAssignmentGroups) {
      setFetchingAssignmentGroups(true);
      createAssignmentGroups();
    }
  }, [
    assignmentGroupsPerAttribute,
    setAssignmentGroupsPerAttribute,
    plateMap,
    selectedAttribute,
    fetchingAssignmentGroups,
  ]);

  return {
    assignmentGroupsPerAttribute,
    setAssignmentGroupsPerAttribute,
    selectedAttribute,
    setSelectedAttribute,
    displayLayer,
  };
}
